import {Injectable} from '@angular/core';
import moment, {Duration} from 'moment';
import {CandlePeriod} from '../../enums/candle-period';
import {parseSecurityKey} from '../../models/security-key';
import {AppConfigService} from '../../services/config.service';
import * as TradingView from './charting-library';
import {
    ChartingLibraryWidgetOptions,
    IChartingLibraryWidget as ChartingLibraryWidget,
    ResolutionString,
    TimeFrameItem
} from './charting-library';
import {TradingViewChartStyleVariablesAccessor} from './trading-view-chart-style-variables-accessor';

export interface TimeFrameSetting {

    /**
     * Format: <integer><y|m|d> ( \d+(y|m|d) as Regex ).
     * Ref: https://github.com/tradingview/charting_library/wiki/Widget-Constructor#time_frames
     * Charting Library API has lots of methods that accept and return resolutions.
     * Ref: https://github.com/tradingview/charting_library/wiki/Resolution
     */
    timeframe: string;

    /**
     * Ref: https://github.com/tradingview/charting_library/wiki/Resolution
     */
    candleSize: string;

    /**
     * The description property was added in v 1.7 and is displayed in the pop-up menu.
     * This parameter is optional. If it isn't specified then the title or text property is used as a description.
     */
    description: string;

    /**
     * The title property was added in v 1.9 and its value will override the default title
     * generated based on the text property. This parameter is optional.
     */
    title?: string;

    /**
     * This property stores duration of timeframe in moment-capable format
     */
    timeframeDuration: Duration;
}

export const TV_CHART_CANDLE_SIZE_BY_PERIOD: Record<CandlePeriod, ResolutionString> = {
    [CandlePeriod.ONE_MINUTE]:      '1' as ResolutionString,
    [CandlePeriod.FIVE_MINUTES]:    '5' as ResolutionString,
    [CandlePeriod.TEN_MINUTES]:     '10' as ResolutionString,
    [CandlePeriod.FIFTEEN_MINUTES]: '15' as ResolutionString,
    [CandlePeriod.THIRTY_MINUTES]:  '30' as ResolutionString,
    [CandlePeriod.HOUR]:            '60' as ResolutionString,
    [CandlePeriod.DAY]:             '1D' as ResolutionString,
    [CandlePeriod.WEEK]:            '1W' as ResolutionString,
    [CandlePeriod.MONTH]:           '1M' as ResolutionString
};

/**
 * Ref: https://github.com/tradingview/charting_library/wiki/Widget-Constructor#time_frames
 */
export const TIME_FRAME_SETTINGS: TimeFrameSetting[] = [
    {
        timeframe: '5y',
        candleSize: TV_CHART_CANDLE_SIZE_BY_PERIOD[CandlePeriod.MONTH],
        description: '5 Years',
        timeframeDuration: moment.duration(5, 'year')
    },
    {
        timeframe: '3y',
        candleSize: TV_CHART_CANDLE_SIZE_BY_PERIOD[CandlePeriod.WEEK],
        description: '3 Years',
        timeframeDuration: moment.duration(3, 'year')
    },
    {
        timeframe: '1y',
        candleSize: TV_CHART_CANDLE_SIZE_BY_PERIOD[CandlePeriod.DAY],
        description: '1 Year',
        timeframeDuration: moment.duration(1, 'year')
    },
    {
        timeframe: '3m',
        candleSize: TV_CHART_CANDLE_SIZE_BY_PERIOD[CandlePeriod.DAY],
        description: '3 Months',
        timeframeDuration: moment.duration(3, 'month')
    },
    {
        timeframe: '1m',
        candleSize: TV_CHART_CANDLE_SIZE_BY_PERIOD[CandlePeriod.HOUR],
        description: '1 Month',
        timeframeDuration: moment.duration(1, 'month')
    },
    {
        timeframe: '7d',
        candleSize: TV_CHART_CANDLE_SIZE_BY_PERIOD[CandlePeriod.HOUR],
        description: '7 Days',
        timeframeDuration: moment.duration(7, 'day')
    },
    {
        timeframe: '3d',
        candleSize: TV_CHART_CANDLE_SIZE_BY_PERIOD[CandlePeriod.THIRTY_MINUTES],
        description: '3 Days',
        timeframeDuration: moment.duration(3, 'day')
    },
    {
        timeframe: '1d',
        candleSize: TV_CHART_CANDLE_SIZE_BY_PERIOD[CandlePeriod.FIFTEEN_MINUTES],
        description: '1 Day',
        timeframeDuration: moment.duration(1, 'day')
    },
    {
        timeframe: '6h',
        candleSize: TV_CHART_CANDLE_SIZE_BY_PERIOD[CandlePeriod.FIVE_MINUTES],
        description: '6 Hours',
        timeframeDuration: moment.duration(6, 'hour')
    },
    {
        timeframe: '1h',
        candleSize: TV_CHART_CANDLE_SIZE_BY_PERIOD[CandlePeriod.ONE_MINUTE],
        description: '1 Hour',
        timeframeDuration: moment.duration(1, 'hour')
    }
];

export const DEFAULT_CANDLE_SIZE = TIME_FRAME_SETTINGS[0].candleSize;

const getChartTimeFrames = (timeFrameSettings: TimeFrameSetting[]): TimeFrameItem[] => timeFrameSettings.map(
    timeFrameSetting => {
        const {
                  timeframe: text,
                  candleSize,
                  description
              } = timeFrameSetting;

        return {
            text,
            resolution: candleSize as ResolutionString,
            description
        };
    }
);

export const getTimeframeSettingByCandleSize = (candleSize: string) =>
    candleSize && TIME_FRAME_SETTINGS.find(timeFrameSetting =>
        timeFrameSetting.candleSize === candleSize.toUpperCase()
    );

export const getTimeframeByCandleSize = (candleSize: string) => {
    const timeFrameSetting = getTimeframeSettingByCandleSize(candleSize);

    return timeFrameSetting
        ? timeFrameSetting.timeframe
        : null;
};

export const getTimeframeSettingByTimeframe = (timeframe: string) =>
    timeframe && TIME_FRAME_SETTINGS.find(timeFrameSetting =>
        timeFrameSetting.timeframe === timeframe.toLowerCase()
    );

export const getCandleSizeByTimeframe = (timeframe: string) => {
    const timeFrameSetting = getTimeframeSettingByTimeframe(timeframe);

    return timeFrameSetting
        ? timeFrameSetting.candleSize
        : DEFAULT_CANDLE_SIZE;
};

@Injectable()
export class TradingViewChartFactory {

    constructor(
        private appConfigService: AppConfigService
    ) {}

    public getChartInstance(
        chartOptions: Partial<ChartingLibraryWidgetOptions>,
        styleVariables: TradingViewChartStyleVariablesAccessor
    ): ChartingLibraryWidget {
        const defaultOptions = this.getDefaultChartOptions();

        const options: ChartingLibraryWidgetOptions = {
            ...defaultOptions,
            ...this.getChartStyleOptions(styleVariables),
            ...chartOptions,

            // Handle undefined in chartOptions.interval.
            interval: chartOptions.interval || defaultOptions.interval
        };

        return new TradingView.widget(options);
    }

    private getDefaultChartOptions(): ChartingLibraryWidgetOptions {
        return {
            fullscreen:        false,
            container_id:      'container_id',
            symbol:            parseSecurityKey(this.appConfigService.appConfig.defaultSecurityKey).symbol,
            interval:          '5' as ResolutionString,
            timeframe:         '6h',
            datafeed:          null,
            library_path:      this.appConfigService.getPublicPath('assets/charting-library') + '/',
            locale:            'en',
            drawings_access:   {type: 'black', tools: [{name: 'Regression Trend'}]},
            disabled_features: [
                'use_localstorage_for_settings',
                'study_templates',
                'header_undo_redo',
                'header_compare',
                'header_chart_type',
                'header_interval_dialog_button',
                'go_to_date',
                'timeframes_toolbar'
            ],
            client_id:         'tradingview.com',
            user_id:           'public_user_id',
            autosize:          true,

            // TODO: Chart styles
            //  Ref.: https://juristr.com/blog/2019/08/dynamically-load-css-angular-cli/
            custom_css_url:    '../../chart-custom-theme.css', // Relative to library_path

            time_frames:       getChartTimeFrames(TIME_FRAME_SETTINGS),
            auto_save_delay:   1, // minimum delay in seconds between autosave events
            theme:             'Dark'
        };
    }

    private getChartStyleOptions(
        styleVariables: TradingViewChartStyleVariablesAccessor
    ): Partial<ChartingLibraryWidgetOptions> {
        return {
            studies_overrides: styleVariables.getMapOfStrings(
                'studies_overrides',
                [
                    'volume.volume.color.0',
                    'volume.volume.color.1'
                ]
            ),
            overrides:         {
                'mainSeriesProperties.hollowCandleStyle.drawWick':   true,
                'mainSeriesProperties.hollowCandleStyle.drawBorder': true,
                'mainSeriesProperties.style':                        9,
                ...styleVariables.getMapOfStrings(
                    'overrides',
                    [
                        'paneProperties.background',
                        'paneProperties.vertGridProperties.color',
                        'paneProperties.horzGridProperties.color',
                        'scalesProperties.backgroundColor',

                        'mainSeriesProperties.hollowCandleStyle.upColor',
                        'mainSeriesProperties.hollowCandleStyle.downColor',
                        'mainSeriesProperties.hollowCandleStyle.borderColor',
                        'mainSeriesProperties.hollowCandleStyle.borderUpColor',
                        'mainSeriesProperties.hollowCandleStyle.borderDownColor',
                        'mainSeriesProperties.hollowCandleStyle.wickColor',
                        'mainSeriesProperties.hollowCandleStyle.wickUpColor',
                        'mainSeriesProperties.hollowCandleStyle.wickDownColor'
                    ]
                ),
                'paneProperties.legendProperties.showSeriesTitle': false,

                /**
                 * TODO: It looks like it is not supported in the current version of TV (1.14)
                 * It should remove the gap from the right on the time scale.
                 * Ref.: https://github.com/tradingview/charting_library/wiki/Overrides
                 * In order to remove the gap in the current version of TV (1.14) the patch is applied to
                 * the library's code in file "library.7caf3f24f35b9ce9c726.js":
                 * 1. method o(TimeScale).prototype.zoomToBarsRange, changed
                 *      1.1 o.prototype.zoomToBarsRange=function(e,t)
                 *      1.2 this.m_rightOffset=t-i
                 * to
                 *      1.1 o.prototype.zoomToBarsRange=function(e,t,t0)
                 *      1.2 this.m_rightOffset=(t0!==undefined?t0:t)-i
                 * 2. method a.prototype.onData, changed
                 *      2.1 case"series_timeframe":...c=void 0!==s&&s?r:r+10,this.m_model.setTimeViewport(o,c);
                 * to
                 *      2.1 case"series_timeframe":...c=void 0!==s&&s?r:r+10,this.m_model.setTimeViewport(o,c,r);
                 * 3. method c.prototype.setTimeViewport, changed
                 *      3.1 c.prototype.setTimeViewport=function(e,t)
                 *      3.2 this.timeScale().zoomToBarsRange(e,t)
                 * to
                 *      3.1 c.prototype.setTimeViewport=function(e,t,t0)
                 *      3.2 this.timeScale().zoomToBarsRange(e,t,t0)
                 * where 10 is a hardcoded gap.
                 */
                'timeScale.rightOffset': 1
            }
        };
    }

}
